import { formatLocalDateTime } from "@topzle/locale";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { LoadingButton } from "../components/LoadingButton";

interface ConsentPreview {
  childName: string;
  guardianEmailHint: string;
  expiresAt: string;
}

export function ParentalConsentPage() {
  const [search] = useSearchParams();
  const token = search.get("token") ?? "";
  const [preview, setPreview] = useState<ConsentPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [guardianDeclaration, setGuardianDeclaration] = useState(false);
  const [acceptedPolicies, setAcceptedPolicies] = useState(false);
  const [approved, setApproved] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) {
      setMessage("This guardian review link is incomplete.");
      setLoading(false);
      return;
    }
    void api
      .parentalConsentPreview(token)
      .then((value) => {
        if (active) setPreview(value);
      })
      .catch((error) => {
        if (active)
          setMessage(
            error instanceof Error ? error.message : "This review link could not be loaded.",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!guardianDeclaration || !acceptedPolicies) return;
    setSubmitting(true);
    setMessage("");
    try {
      await api.approveParentalConsent({
        token,
        guardianDeclaration: true,
        acceptedTerms: true,
        acceptedPrivacy: true,
      });
      setApproved(true);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Approval could not be saved.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <section className="empty-state">Loading the guardian review…</section>;

  return (
    <section className="auth-page parental-review-page">
      <form className="auth-card parental-review-card" onSubmit={(event) => void submit(event)}>
        <span className="icon-tile">{approved ? <CheckCircle2 /> : <ShieldCheck />}</span>
        <span className="eyebrow">PARENT OR GUARDIAN REVIEW</span>
        {approved ? (
          <>
            <h1>Approval saved.</h1>
            <p>
              {preview?.childName ?? "The young player"} can sign in again to refresh their safe
              account safety review. Age-appropriate chat rules and platform call controls still
              apply, while public and private games remain available.
            </p>
            <Link className="button button-primary wide" to="/">
              Done
            </Link>
          </>
        ) : preview ? (
          <>
            <h1>Review {preview.childName}’s Topzle account.</h1>
            <p>
              Social rooms stay locked until you approve. The review was sent to{" "}
              <strong>{preview.guardianEmailHint}</strong> and expires{" "}
              {formatLocalDateTime(preview.expiresAt)}.
            </p>
            <div className="parental-review-summary">
              <strong>What approval enables</strong>
              <span>Private, age-appropriate social games with Topzle safety protections.</span>
              <span>Restricted preset chat for young players.</span>
              <span>No voice or video access until the adult age threshold is met.</span>
            </div>
            <label className="consent-check">
              <input
                type="checkbox"
                checked={guardianDeclaration}
                onChange={(event) => setGuardianDeclaration(event.target.checked)}
                disabled={submitting}
              />
              <span>
                I confirm that I am this player’s parent or legal guardian and consent to this
                account using the age-appropriate social features described above.
              </span>
            </label>
            <label className="consent-check">
              <input
                type="checkbox"
                checked={acceptedPolicies}
                onChange={(event) => setAcceptedPolicies(event.target.checked)}
                disabled={submitting}
              />
              <span>
                I have reviewed the <Link to="/terms">Terms</Link>,{" "}
                <Link to="/privacy">Privacy Policy</Link>, and <Link to="/safety">Safety</Link>{" "}
                information.
              </span>
            </label>
            <LoadingButton
              type="submit"
              className="button button-primary wide"
              loading={submitting}
              loadingLabel="Saving approval…"
              disabled={!guardianDeclaration || !acceptedPolicies}
            >
              Approve account safety review
            </LoadingButton>
          </>
        ) : (
          <>
            <h1>This link cannot be used.</h1>
            <p>{message}</p>
            <Link className="button button-primary wide" to="/">
              Go to Topzle
            </Link>
          </>
        )}
        {preview && message && (
          <div className="status-message" role="status">
            {message}
          </div>
        )}
      </form>
    </section>
  );
}
